import { NewTimetableActionModel } from "./../models/timetable";
import {makeAutoObservable} from "mobx";
import {TimetableActionModel, TimetableActionTypes} from "../models/timetable";
import {DateUtils} from "../../helpers/dateUtils";
import {ActionInStore, IActionInStore} from "./actionInStore";
import {FerryDropDownModel} from "../../dictionaries/models/ferry";
import {PortModel} from "../../dictionaries/models/ports";
import {RouteDropDownModel} from "../../topology/models/route";

export interface ITimetableStore {
    clearStore: () => void;
    clearYearAndWeek: () => void;
    createAction: () => void;
    removeAction: (actionId: number) => void;
    actions: IActionInStore[];
    week: number;
    year: number;
    setWeekAndYearStore: (week: number, year: number) => void;
    ferries: FerryDropDownModel[];
    setFerries: (ferries: FerryDropDownModel[]) => void;
    routes: RouteDropDownModel[];
    setRoutes: (routes: RouteDropDownModel[]) => void;
    ports: PortModel[];
    setPorts: (ports: PortModel[]) => void;
    setTimetableStore: (actionData: TimetableActionModel | undefined) => void;
    editElement: string;
    setEditElement: (editElemen: string) => void;
    lastAddElement: NewTimetableActionModel | undefined;
    setLastAddElement: (addElement: NewTimetableActionModel | undefined) => void;
}

export class TimetableStore implements ITimetableStore {
    _actions: IActionInStore[] = [];
    _week = 0;
    _year = 0;
    _ferries: FerryDropDownModel[] = [];
    _routes: RouteDropDownModel[] = [];
    _ports: PortModel[] = [];
    _editElement = "";
    _lastAddElement: NewTimetableActionModel | undefined = undefined;

    constructor() {
        makeAutoObservable(this);

        this.createAction();
    }

    createAction() {
        this._actions.push(new ActionInStore());
    }

    removeAction(actionId: number) {
        this._actions.splice(actionId, 1);
    }

    clearStore() {
        this._actions = [];
        this.createAction();
    }

    clearYearAndWeek () {
        this._week = 0;
        this._year = 0;
    }

    get actions() {
        return this._actions;
    }

    get week() {
        return this._week;
    }

    get year() {
        return this._year;
    }

    setWeekAndYearStore(week: number, year: number) {
        if (!week || !year) {
            return;
        }

        this._week = week;
        this._year = year;
    }

    get ferries() {
        return this._ferries;
    }

    setFerries(ferries: FerryDropDownModel[]) {
        this._ferries = ferries;
    }

    get routes() {
        return this._routes;
    }

    setRoutes(routes: RouteDropDownModel[]) {
        this._routes = routes;
    }

    get ports() {
        return this._ports;
    }

    setPorts(ports: PortModel[]) {
        this._ports = ports;
    }

    get editElement() {
        return this._editElement;
    }

    setEditElement(editElement: string) {
        this._editElement = editElement;
    }

    get lastAddElement() {
        return this._lastAddElement;
    }

    setLastAddElement(addElement: NewTimetableActionModel | undefined) {
        this._lastAddElement = addElement;
    }

    setTimetableStore(actionData: TimetableActionModel | undefined) {
        if (!actionData) {
            return;
        }

        if (this.ferries?.map(ferry => ferry.id).includes(actionData.ferry?.id)) {
            this.actions[0].setFerryId(actionData?.ferry?.id || "");
        }

        this.actions[0].setActionType(actionData?.actionType || TimetableActionTypes.ARRIVAL);
        this.actions[0].setVoyageNo(actionData?.voyageNo || "");
        this.actions[0].setWharfId(actionData?.wharf?.id || "");
        this.actions[0].setNotes(actionData?.notes || "");

        this.actions[0].setArrivalDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.arrivalTime || null));
        this.actions[0].setArrivalHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.arrivalTime || null));
        this.actions[0].setDepartureDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.departureTime || null));
        this.actions[0].setDepartureHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.departureTime || null));

        this.actions[0].setActualArrivalDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.actualArrivalTime || null));
        this.actions[0].setActualArrivalHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.actualArrivalTime || null));
        this.actions[0].setActualDepartureDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.actualDepartureTime || null));
        this.actions[0].setActualDepartureHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.actualDepartureTime || null));

        const routes: string[] = [];
        if (actionData.routes && actionData.routes.length && this.routes && this.routes.length) {
            actionData.routes.map((routeFromAction: RouteDropDownModel) => {
                if (this.routes.map(routeFromDictionary => routeFromDictionary.id).includes(routeFromAction.id)) {
                    routes.push(routeFromAction.id);
                }
            });
        }

        this.actions[0].setRoutes(routes);

        this.actions[0].setOpenParkingDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.openParkingTime || null));
        this.actions[0].setOpenParkingHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.openParkingTime || null));
        this.actions[0].setStartLoadingDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.startLoadingTime || null));
        this.actions[0].setStartLoadingHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.startLoadingTime || null));
        this.actions[0].setEndLoadingDate(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.endLoadingTime || null));
        this.actions[0].setEndLoadingHour(DateUtils.getDateAndTimeInPolishTimeZone(actionData?.endLoadingTime || null));

        if (actionData.destinationPort && this.ports?.map(port => port.id).includes(actionData.destinationPort?.id)) {
            this.actions[0].setDestinationPortId(actionData?.destinationPort?.id || "");
        }

        this.actions[0].setHasOrderRegistered(actionData.hasOrderRegistered);
    }
}
